import { ICreator } from "@shared/lib/types";
import { Flex, Form, Input, Radio, Row, Spin } from "antd";
import { Button } from "../Button";
import { useGetBriefs } from "@entities/brief/lib/hooks/useGetBriefs";
import { useState, useEffect } from "react";
import { useSuggestInfluencer } from "@entities/brief/lib/hooks/useSuggestInfluencer";
import { ToastService } from "@shared/lib/services";
import InfiniteScroll from "react-infinite-scroll-component";

const SuggestCampaignForm = ({
  creators,
  onClose,
}: {
  creators: ICreator[];
  onClose: () => void;
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [pageNo, setPageNo] = useState(1);
  const [briefOptions, setBriefOptions] = useState<any[]>([]);
  const [selectedBrief, setSelectedBrief] = useState<string | undefined>(
    undefined
  );
  const [hasMore, setHasMore] = useState(true);
  const { isFetching, briefs, totaldata } = useGetBriefs({
    pageNo,
    limit: 20,
    searchQuery,
  });
  const { suggest, isSuggested, isSuggesting } = useSuggestInfluencer("");
  const [form] = Form.useForm();
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (briefs && pageNo === 1) {
      setBriefOptions(briefs);
    } else if (briefs && pageNo > 1) {
      setBriefOptions((prev) => [...prev, ...briefs]);
    }
    setHasMore(briefOptions.length + (briefs?.length || 0) < totaldata);
    // eslint-disable-next-line
  }, [briefs]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPageNo(1);
    setBriefOptions([]);
  };

  const fetchMoreData = () => {
    if (!isFetching && hasMore) {
      setPageNo((prev) => prev + 1);
    }
  };

  const handleSubmit = () => {
    if (!selectedBrief) return;
    const data = {
      briefId: selectedBrief,
      addBySocialMediaObjectIds: creators.map((creator) => ({
        socialMediaObjectId: creator.socialMediaObjectId,
        estViews: 0,
      })),
    };
    suggest({ data });
  };

  useEffect(() => {
    if (isSuggested) {
      ToastService.success("Suggested in brief");
      onClose();
    }
    // eslint-disable-next-line
  }, [isSuggested]);

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Form.Item label="Search by brief or brand name">
          <Input.Search
            placeholder="Search by brief or brand name"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onSearch={handleSearch}
            enterButton
            allowClear
          />
        </Form.Item>
        <Form.Item
          name="briefId"
          label="Select a brief"
          rules={[{ required: true, message: "Brief is required" }]}
        >
          <div
            id="brief-infinite-scroll"
            style={{
              maxHeight: 300,
              overflow: "auto",
              border: "1px solid #eee",
              borderRadius: 8,
              padding: 8,
            }}
          >
            {isFetching && pageNo === 1 && (
              <Row justify="center" style={{ margin: "20px auto" }}>
                <Spin />
              </Row>
            )}
            <InfiniteScroll
              dataLength={briefOptions.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={<Spin />}
              scrollableTarget="brief-infinite-scroll"
            >
              <Radio.Group
                value={selectedBrief}
                onChange={(e) => setSelectedBrief(e.target.value)}
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                {briefOptions.map((brief) => (
                  <Radio
                    key={brief._id}
                    value={brief._id}
                    style={{ marginBottom: 12 }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                      <img
                        src={brief.brand?.logoUrl}
                        alt={brief.brand?.name}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          objectFit: "contain",
                          background: "#fff",
                          border: "1px solid #eee",
                        }}
                      />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 16 }}>
                          {brief.name}
                        </div>
                        <Flex align="center" gap={4}>
                          <div style={{ color: "#888", fontSize: 12 }}>
                            {brief.brandName}
                          </div>
                          {brief?.mainStatus && (
                            <span
                              style={{
                                marginLeft: 8,
                                border: "1px solid #d6e4ff",
                                color: "#2f54eb",
                                background: "#f0f5ff",
                                borderRadius: 12,
                                padding: "2px 12px",
                                fontWeight: 500,
                                fontSize: 10,
                              }}
                            >
                              {brief?.mainStatus}
                            </span>
                          )}
                        </Flex>
                      </div>
                    </div>
                  </Radio>
                ))}
              </Radio.Group>
            </InfiniteScroll>
          </div>
        </Form.Item>
      </div>
      <div
        style={{
          display: "flex",
          gap: "5px",
          justifyContent: "flex-end",
          marginTop: "16px",
        }}
      >
        <Form.Item>
          <Button disabled={isSuggesting} onClick={onClose} htmlType="button">
            Cancel
          </Button>
        </Form.Item>
        <Form.Item>
          <Button
            disabled={isSuggesting || !selectedBrief}
            loading={isSuggesting}
            type="primary"
            htmlType="submit"
          >
            {isSuggesting ? "Suggesting..." : "Suggest"}
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default SuggestCampaignForm;
